import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, ArrowUpRight, Download } from 'lucide-react';
import { useEffect } from 'react';

const technologiesData = {
  endoliftx: {
    name: 'EndoliftX',
    tagline: 'The Laser Evolution of Lifting',
    logo: '/endoliftx-logo.png',
    heroImage: '/endoliftx-hero.jpg',
    video: '/endoliftx-video.mp4',
    externalLink: 'https://www.endoliftx.com/en/',
    brochure: '/endoliftx-brochure.pdf',
    description: 'EndoliftX® is the non-surgical lifting treatment for immediate, long-lasting, and natural results. Advanced laser technology delivering superior clinical outcomes with minimal pain and post-treatment impact.',
    longDescription: 'EndoliftX® is a medical laser treatment performed exclusively with LASEmaR® 1500 to reshape deep and superficial skin layers, firm and retract connective septa, stimulate new collagen production, and when necessary, reduce excess fat. Using advanced 1470nm wavelength technology, EndoliftX® targets the dermis and subcutaneous tissue with precision, delivering results that continue to improve over time.',
    benefits: [
      'Immediately visible results',
      'Continuous improvement in subsequent months',
      'Compatible with other procedures',
      'Single session treatment',
      'Minimal or no recovery time',
      'Stimulates new collagen production',
    ],
    applications: ['Face lifting', 'Neck tightening', 'Jawline contouring', 'Submental (double chin)', 'Inner arms', 'Abdomen', 'Buttocks', 'Inner thighs', 'Knees', 'Ankles'],
    stats: [
      { value: '10+', label: 'Years Research' },
      { value: '1', label: 'Session Needed' },
      { value: '0', label: 'Downtime' },
    ],
    treatments: ['Endolift®', 'EndoliftX®', 'EndoliftXs', 'LipoLaserMax', 'LipoLaserX'],
  },
  jalupro: {
    name: 'Jalupro',
    tagline: 'Revitalize. Regenerate. Rediscover.',
    logo: '/jalupro-logo.png',
    heroImage: '/jalupro-hero.jpg',
    description: 'Harness the power of Collagen and Amino Acids Replacement Therapy to support natural beauty, skin vitality and let your inner beauty glow. Trusted by professionals for 20+ years, designed for life.',
    longDescription: 'Jalupro combines scientific innovation with proven results to provide effective skincare solutions that promote long-lasting skin health and beauty. Jalupro\'s unique formulations are designed to support collagen production and rejuvenate connective tissue, enhancing the skin\'s texture and appearance. The range includes Classic, HMW, Super Hydro, Young Eye, and Glow Peel formulations.',
    benefits: [
      'Stimulates fibroblast activity',
      'Enhances natural collagen synthesis',
      'Improves skin elasticity and tone',
      'Reduces fine lines and wrinkles',
      'Suitable for all skin types',
      'Multiple formulations for targeted treatment',
    ],
    applications: ['Facial rejuvenation', 'Eye area treatment', 'Neck and décolletage', 'Hand rejuvenation', 'Skin hydration', 'Anti-aging protocols', 'Scar management', 'Hair revitalization'],
    stats: [
      { value: '20+', label: 'Years Trusted' },
      { value: '5', label: 'Product Range' },
      { value: '60+', label: 'Countries' },
    ],
    products: ['Jalupro Classic', 'Jalupro HMW', 'Jalupro Super Hydro', 'Jalupro Young Eye', 'Jalupro Glow Peel'],
    externalLink: 'https://jalupro.co.nz',
    brochure: '/jalupro-brochure.pdf',
    productDetails: [
      {
        name: 'Jalupro Classic',
        image: '/jalupro-classic.png',
        description: 'JALUPRO CLASSIC is your go-to choice for body skin biorevitalization, such as tummy, arms, inner thighs or knees. It is as well indicated for scars treatment (the story of JALUPRO started in wound healing) and hair strengthening.',
        features: [
          'Combines amino acids and low molecular weight hyaluronic acid',
          'Increases collagen production, providing fibroblasts with collagen building blocks',
          'Improves skin structure and supports tissue regeneration',
          'Effective for treating early signs of aging, scarring, and scalp health',
        ],
      },
      {
        name: 'Jalupro HMW',
        image: '/jalupro-hmw.png',
        description: 'For areas that require skin rejuvenation and hydration such as face, neck, décolleté and hands, JALUPRO HMW combines the power of Amino Acids with hydration. It is as well the treatment for lips rejuvenation, when you want young looking lips without additional volume.',
        features: [
          'The Amino Acids cluster will increase collagen production providing building blocks of collagen to fibroblasts',
          'High Molecular Weight Hyaluronic Acid will provide hydration',
          'Skin quality is restored, looks healthy, happy and radiant',
        ],
      },
      {
        name: 'Jalupro Super Hydro',
        image: '/jalupro-superhydro.jpg',
        description: 'Offers deep connective tissue support especially facial ligament support for a good facial structure and lifting maintenance.',
        features: [
          'A tailored formulation combining our signature Amino Acids cluster for additional benefits',
          'High viscosity product for ligaments mechanical support',
          'Supports structural regeneration of ligaments and deep connective tissue such as retinacular cutis',
        ],
      },
      {
        name: 'Jalupro Young Eye',
        image: '/jalupro-youngeye.avif',
        description: 'Skin of the eye area is the thinnest in the face and ages faster. It requires a bespoke formulation for revitalization of this delicate periocular area.',
        features: [
          'Contains amino acids to improve collagen production, skin density and thickness',
          'Reduces periorbital wrinkles, dark circles and bags when related to thinning and sagging skin',
          'Bi-density hyaluronic acid ensures hydration without increasing puffiness',
        ],
      },
      {
        name: 'Jalupro Glow Peel',
        image: '/jalupro-glowpeel.png',
        description: 'Jalupro Glow Peel is an innovative peeling treatment that stands out due to its unique formulation and versatile application. The effective combination of high-quality ingredients gently removes dead skin cells, stimulates collagen production, and promotes natural skin regeneration.',
        features: [
          'Visibly refined skin texture with increased elasticity and firmness',
          'Targeted solution for fine lines, wrinkles, pigmentation disorders, or acne scars',
          'Suitable for all skin types',
        ],
      },
    ],
  },
  aqualyx: {
    name: 'Aqualyx',
    tagline: 'The World\'s Leading Fat Dissolving Solution',
    heroImage: '/aqualyx-hero.png',
    brochure: '/aqualyx-brochure.pdf',
    description: 'Aqualyx Cosmetic is a safe, effective, and painless non-surgical treatment designed to contour targeted areas of the body. With just a few simple sessions, it helps refine and enhance body shape.',
    longDescription: 'Aqualyx Cosmetic has been on the market for 15 years, selling over 4,000,000 vials, improving patients\' bodies and confidence all over the world. Founded by Professor Pasquale Motolese, Aqualyx is made from the deoxycholate family of acids. Each package is provided with a digitally numbered hologram that allows its total traceability, ensuring authenticity and quality.',
    benefits: [
      'Non-surgical body contouring solution',
      'Targeted results in specific areas',
      'Safe and effective treatment',
      'Painless procedure',
      'Visible results within first two treatments',
      'Made in Italy with premium quality',
    ],
    applications: ['Double chin', 'Bra fat', 'Love handles', 'Inner thighs', 'Knee area', 'Upper arms', 'Abdomen', 'Hips'],
    stats: [
      { value: '4M+', label: 'Vials Sold' },
      { value: '15', label: 'Years On Market' },
      { value: '65+', label: 'Countries' },
    ],
    productImage: '/aqualyx-product.png',
    resultsImage: '/aqualyx-results.png',
    evolutionProduct: {
      name: 'Aqualyx Face Kit',
      image: '/aqualyx-face.jpg',
      resultsImage: '/aqualyx-face-results.png',
      duoImage: '/aqualyx-duo.png',
      intro: 'Born from the legacy of Aqualyx, the world\'s leading body contouring treatment, the Aqualyx Face Kit is an advanced system designed to address the unique challenge of a double chin. It helps achieve a more sculpted and defined facial profile.',
      subtitle: 'What is Aqualyx Face',
      description: 'Aqualyx Face Kit is more than just another cosmetic. It is a complete system that includes Seralyx, a highly specific serum containing Physale, a soothing compound with soothing properties similar to corticoids, Resveratrol, with its potent antioxidant activity and a patented mix of low and medium molecular weight hyaluronic acids to improve the cell viability.',
    },
  },
  alidya: {
    name: 'Alidya',
    tagline: 'Anti-Cellulite Treatment',
    heroImage: '/alidya-hero.png',
    description: 'Alidya is the first injectable device specifically designed for the prevention and treatment of Cellulite. Over 90% of women suffer from Cellulite — restore confidence & beauty to your body.',
    longDescription: 'Alidya was founded by Professor Pasquale Motolese. His passion for improving people\'s lives from within led him to begin his research on an innovative method to treat unwanted cellulite. After years of research and studies, a new innovative cellulite eliminating formula was created. Alidya has been on the market for over 10 years, helping thousands of women become the best version of themselves.',
    benefits: [
      'First injectable cellulite treatment',
      'Smooths & tightens skin',
      'Improves lymphatic drainage',
      'Removes toxins from skin tissue',
      'Produces long-lasting results',
      'Visible reduction in cellulite',
    ],
    applications: ['Thighs', 'Buttocks', 'Abdomen', 'Upper arms', 'Hips', 'Knee area'],
    stats: [
      { value: '1st', label: 'Injectable Solution' },
      { value: '10+', label: 'Years On Market' },
      { value: '90%', label: 'Women Affected' },
    ],
    productImage: '/alidya-product.png',
    resultsImage: '/alidya-results.png',
    brochure: '/alidya-brochure.pdf',
  },
  mct: {
    name: 'MCT',
    tagline: 'Meta Cell Technology',
    heroImage: '/mct-hero.png',
    video: '/mct-video.mp4',
    externalLink: 'https://metacelltech.com/',
    description: 'MCT is a system designed to enhance autologous products such as PRP (Platelet-Rich Plasma), while releasing the patient\'s exosomes in a single session. Photothermal Biomodulation for regenerative medicine.',
    longDescription: 'MCT System is composed of two certified medical devices (MCT Unit & MCT Kit) using photothermal biomodulation to maximize the number of exosomes available, ensuring optimal results. By leveraging this advanced technology, MCT System provides high-quality exosomes naturally, enhancing the therapeutic potential. The system works with any commercial PRP kit to boost existing regenerative medicine treatments.',
    benefits: [
      'Increased growth factor release',
      'Enhanced ATP production',
      'Augmented regenerative power',
      'Improved platelet performance',
      'More comfortable injection',
      'Effective and natural results',
    ],
    applications: ['PRP enhancement', 'Cells regeneration', 'Exosome stimulation', 'Global skin rejuvenation', 'Anti-aging protocols', 'Post-procedure recovery'],
    stats: [
      { value: '3', label: 'Easy Steps' },
      { value: '2', label: 'Medical Devices' },
      { value: '100%', label: 'Autologous' },
    ],
    presets: ['PRP Preset - Increase platelet performance', 'Cells Preset - Generate more ATP from mitochondria', 'Exosomes Preset - Promotes exosomes release'],
    productDetails: [
      {
        name: 'MCT Unit\u00AE',
        image: '/mct-unit.png',
        description: 'MCT Unit\u00AE enables existing regenerative medicine treatments to be boosted, simply and quickly. Through the application of electromagnetic and thermal energy, it improves the general characteristics of autologous products, such as growth factor content, ATP production, and cell or platelet performance. It handles a wide range of temperatures and multiple wavelengths which are set up in comfortable one-touch presets.',
        brochure: '/mct-protocols-brochure.pdf',
        brochureLabel: 'Download MCT Protocols',
        features: [
          'Electromagnetic and thermal energy application',
          'Improves growth factor content',
          'Enhances ATP production and cell performance',
          'Multiple wavelengths with one-touch presets',
        ],
      },
      {
        name: 'MCT Kit\u00AE',
        image: '/mct-kit.jpg',
        description: 'MCT Kit\u00AE is a patented device with a specific shape and chemical composition, exclusively developed for photo-thermal conditioning. It is made of a medical grade polymer that guarantees optimal scattering, transmittance, and other optical properties, ensuring the emitted light will reach the target effectively. Its unique geometry grants an excellent surface/volume energy exposure ratio. MCT Kit\u00AE can allocate 10 mL of any autologous preparate.',
        features: [
          'Patented design for photo-thermal conditioning',
          'Medical grade polymer with optimal optical properties',
          'Excellent surface/volume energy exposure ratio',
          'Allocates 10 mL of any autologous preparate',
        ],
      },
      {
        name: 'Exo-Tech \u2014 The First Exosome Bioimplant',
        image: '/exo-tech.jpg',
        description: 'Exo-Tech represents a new class of autologous bioimplants designed for structural support and sustained regenerative signaling. By combining platelet-poor plasma (PPP) with PRP enriched in platelet-derived exosomes from the MCT System\u00AE, Exo-Tech creates a viscoelastic, injectable matrix that merges mechanical lift with biological regeneration.',
        brochure: '/exo-tech-brochure.pdf',
        brochureLabel: 'Download Exo-Tech Brochure',
        features: [
          'First autologous exosome bioimplant',
          'Combines PPP with exosome-enriched PRP',
          'Viscoelastic injectable matrix',
          'Merges mechanical lift with biological regeneration',
        ],
      },
    ],
  },
  jbp: {
    name: 'JBP',
    tagline: 'Nanoneedle & Nanocannula',
    heroImage: 'https://images.unsplash.com/photo-1668453557069-023d287d22f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxtZWRpY2FsJTIwbmVlZGxlJTIwY2FubnVsYSUyMGFlc3RoZXRpYyUyMGluamVjdGlvbiUyMGRldmljZXxlbnwwfHx8fDE3NzUzMDA4OTV8MA&ixlib=rb-4.1.0&q=85',
    description: 'Premium Japanese precision injection devices. JBP Nanoneedles and Nanocannulas are designed for minimally invasive aesthetic procedures with maximum patient comfort.',
    longDescription: 'JBP (Japan Bio Products) represents the pinnacle of Japanese precision engineering in medical devices. The Nanoneedle and Nanocannula range offers ultra-fine injection devices that minimize patient discomfort while maximizing precision for aesthetic practitioners. These devices are essential tools for dermal fillers, mesotherapy, and other injectable treatments.',
    benefits: [
      'Ultra-fine gauge for minimal pain',
      'Japanese precision manufacturing',
      'Reduced bruising and swelling',
      'Improved product distribution',
      'Enhanced patient comfort',
      'Versatile for multiple treatments',
    ],
    applications: ['Dermal fillers', 'Mesotherapy', 'PRP injections', 'Biorevitalization', 'Skin boosters', 'Fat dissolving injections'],
    stats: [
      { value: 'Ultra', label: 'Fine Gauge' },
      { value: 'Nano', label: 'Technology' },
      { value: '100%', label: 'Precision' },
    ],
    products: ['JBP Nanoneedle', 'JBP Nanocannula'],
    brochures: [
      { label: 'Nanoneedle Brochure', file: '/jbp-nanoneedle-brochure.pdf' },
      { label: 'Nanocannula Brochure', file: '/jbp-nanocannula-brochure.pdf' },
    ],
  },
  promoitalia: {
    name: 'Promoitalia',
    tagline: 'Advanced Skin Solutions',
    heroImage: '/promoitalia-logo.png',
    description: 'Promoitalia is a world-renowned Italian pharmaceutical company specialising in advanced aesthetic solutions. Offering Rich PL, Skinfill Bacio, and Skinfill Blue for comprehensive skin rejuvenation.',
    longDescription: 'Promoitalia has been at the forefront of aesthetic medicine innovation for decades, developing cutting-edge formulations that deliver exceptional results. Their product range addresses a wide spectrum of aesthetic needs, from deep skin revitalisation to precise dermal filling, all manufactured to the highest Italian pharmaceutical standards.',
    benefits: [
      'Italian pharmaceutical excellence',
      'Comprehensive product range',
      'Clinically proven formulations',
      'Advanced skin rejuvenation',
      'Precise dermal filling solutions',
      'Trusted by practitioners worldwide',
    ],
    applications: ['Skin rejuvenation', 'Dermal filling', 'Volume restoration', 'Fine lines treatment', 'Lip enhancement', 'Facial contouring'],
    stats: [
      { value: 'Leaders', label: 'In Innovation' },
      { value: 'Italy', label: 'Made In' },
      { value: 'Global', label: 'Reach' },
    ],
    products: ['Rich PL', 'Skinfill Bacio', 'Skinfill Blue'],
    productDetails: [
      {
        name: 'Rich PL Advance',
        image: '/rich-pl.jpg',
        description: 'A new concept, a new formulation. A unique and high-performance mixture of ingredients capable of restructuring the dermal tissue quickly, effectively and long-lasting. The fusion of the 4 most powerful Bio-Stimulators. The first ready-to-use solution combining PLLA, HA, Recombinant collagen type III, and CaHa for unparalleled dermal support and regeneration.',
        brochure: '/rich-pl-brochure.pdf',
        brochureLabel: 'Download Rich PL Brochure',
        resultsImage: '/rich-pl-results.png',
        features: [
          'PLLA (Poly-L-Lactic Acid)',
          'CA-HA (Calcium Hydroxyapatite)',
          'Recombinant collagen type III',
          'Free Hyaluronic Acid',
          'Immediate hydration and long-term volumizing effect',
        ],
      },
      {
        name: 'Skinfill Bacio',
        image: '/skinfill-bacio.jpg',
        description: 'Promoitalia Skinfill Bacio \u2013 the perfect symphony between Vitamin B12 and HA for lips. Powerful Duo of Definition and Super hydration. It works by intensely hydrating the lips, providing hydration and bio-restructuring for plumpness and definition without changing the overall structure of the lips.',
        brochure: '/skinfill-bacio-brochure.pdf',
        brochureLabel: 'Download Skinfill Bacio Brochure',
        resultsImage: '/skinfill-bacio-results.png',
        features: [
          'Supercharge hydration',
          'Stimulates collagen and elastin production',
          'Refine lip definition',
          'Vitamin B12 and free HA at 20mg/ml concentration',
          'Plumpness without changing lip structure',
        ],
        sections: [
          {
            title: 'Key Ingredients',
            items: [
              'Vitamin B12 and free HA in a differential matrix with fractioned molecular weight at 20mg/ml',
              'Hyaluronic acid provides deep hydration and reduces signs of ageing',
              'Vitamin B12 is vital in cell reproduction and provides essential nutrients for collagen production',
              'Creates firm, supple and plump skin and improves elastin production',
            ],
          },
        ],
      },
      {
        name: 'Skinfill Blue',
        image: '/skinfill-blue.avif',
        description: 'Promoitalia Skinfill Blue is a skin booster composed, especially for men, of a hyper-concentrated mixture of hyaluronic acid with three different molecular weights, enriched with synthetic exosomal structures rich in biomimetic peptides. Its formulation boasts the highest concentration of Hyaluronic acid achieved on the market, allowing it to target thicker skin and thus conferring outstanding hydration and an anti-aging effect.',
        resultsImage: '/skinfill-blue-results.png',
        features: [
          'Concentration: 70 mg in 2 ml HA',
          'Cluster of 3 stable molecular weights HA',
          'Exosomal Technology: synthetic exosomes for delivering HA and Peptides',
          'Biomimetic peptides and Growth factors for cellular stimulation',
          'Immediate increase of skin brightness and instant hydration',
          'Long-lasting bio-restructuring effect',
          'R-AiNet Pro technology for extended release over time',
          'Especially formulated for thicker male skin',
        ],
        sections: [
          {
            title: 'Indications',
            items: [
              'Enhances skin quality, facial, neck, and d\u00e9collet\u00e9 skin elasticity',
              'Reduces sagging and improves texture',
              'Stimulates cellular reproduction through growth factors and biomimetic peptides',
              'Multi-layer remodeling at various tissue levels, promoting cell vitality',
              'Richer in collagen production',
            ],
          },
          {
            title: 'Key Ingredients',
            items: [
              'Copper Tripeptide',
              'Epidermal Growth Factor',
              'Insulin Like Growth Factor',
              'Acid Fibroblast Growth Factor',
              'Basic Fibroblast Growth Factor',
              'Vascular Epidermal Growth Factor',
              'Acetylglutamine and Folic Acid complex',
            ],
          },
          {
            title: 'Treatment Protocol',
            items: [
              '4\u20136 sessions spaced 15 days apart',
              'Maintenance session every 2 months to preserve results',
            ],
          },
        ],
      },
    ],
  },
  dqparis: {
    name: 'DQ Paris',
    tagline: 'Aesthetic Innovation from Paris',
    heroImage: '/dqparis-hero.webp',
    brochure: '/blue-booster-brochure.pdf',
    description: 'BLUE BOOSTER is a professional skin revitalizing booster designed to help support visible skin firmness, hydration, and radiance during advanced in-clinic aesthetic skincare protocols.',
    longDescription: 'BLUE BOOSTER is a concentrated peptide complex designed to restore the skin\u2019s regenerative capacity. Powered by Copper Tripeptide (GHK-Cu), it revitalizes the skin to improve firmness and cellular turnover. Enriched with Madecassoside and Ectoin, the formula helps strengthen the skin barrier, support stability, and enhance peptide performance for visible anti-aging and regenerative results.',
    benefits: [
      'Supports collagen and elastin maturation',
      'Reinforces extracellular matrix architecture',
      'Improves firmness and tissue resilience',
      'Protects against enzymatic matrix degradation',
      'Promotes visible skin regeneration',
    ],
    applications: ['Face', 'Neck'],
    stats: [
      { value: '1', label: 'Flagship Product' },
      { value: 'Paris', label: 'Origin' },
      { value: 'Premium', label: 'Quality' },
    ],
    products: ['Blue Booster'],
    productDetails: [
      {
        name: 'Blue Booster',
        image: '/blue-booster-product.webp',
        description: 'BLUE BOOSTER is a concentrated peptide complex designed to restore the skin\u2019s regenerative capacity. Powered by Copper Tripeptide (GHK-Cu), it revitalizes the skin to improve firmness and cellular turnover. Enriched with Madecassoside and Ectoin, the formula helps strengthen the skin barrier, support stability, and enhance peptide performance for visible anti-aging and regenerative results.',
        features: [
          'Supports collagen and elastin maturation',
          'Reinforces extracellular matrix architecture',
          'Improves firmness and tissue resilience',
          'Protects against enzymatic matrix degradation',
          'Promotes visible skin regeneration',
          'Each box contains 1 sterile vial \u00d7 238 mg / 0.0084 oz.',
        ],
        sections: [
          {
            title: 'Composition',
            items: [
              'Copper Tripeptide-1 (GHK-Cu)',
              'Madecassoside',
              'Ectoin',
            ],
          },
        ],
      },
    ],
  },
};

const TechnologyPage = () => {
  const { slug } = useParams();
  const technology = technologiesData[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!technology) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="heading-2 mb-4">Technology Not Found</h1>
          <Link to="/" className="btn-primary">Return Home</Link>
        </div>
      </div>
    );
  }

  return (
    <main data-testid={`technology-page-${slug}`}>
      {/* Hero Section */}
      <section className="relative min-h-[70vh] md:min-h-[80vh] flex items-end">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src={technology.heroImage}
            alt={technology.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A]/40 to-transparent" />
        </div>

        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="absolute top-28 md:top-32 left-6 md:left-12 lg:left-24"
        >
          <Link
            to="/#technologies"
            className="flex items-center gap-3 text-white/80 hover:text-white transition-colors group"
            data-testid="back-to-technologies"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="overline">Back to Technologies</span>
          </Link>
        </motion.div>

        {/* Hero Content */}
        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24 w-full">
          {technology.logo && (
            <motion.img
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              src={technology.logo}
              alt={`${technology.name} logo`}
              className="h-16 md:h-20 w-auto mb-6 object-contain brightness-0 invert"
            />
          )}
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="overline text-white/60 mb-4 block"
          >
            {technology.tagline}
          </motion.span>
          
          {!technology.logo && (
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="heading-1 text-white mb-6"
              data-testid="technology-name"
            >
              {technology.name}
            </motion.h1>
          )}
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="body-text text-white/80 text-lg max-w-2xl"
          >
            {technology.description}
          </motion.p>

          {technology.externalLink && (
            <motion.a
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              href={technology.externalLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 mt-8 btn-primary bg-white text-[#1A1A1A] hover:bg-[#F7E7CE] group"
              data-testid="external-link-btn"
            >
              <span>Visit Official Website</span>
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </motion.a>
          )}

          {technology.brochure && (
            <motion.a
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              href={technology.brochure}
              download
              className="inline-flex items-center gap-3 mt-8 btn-primary bg-white text-[#1A1A1A] hover:bg-[#F7E7CE] group"
              data-testid="brochure-download-btn"
            >
              <span>Download Brochure</span>
              <Download className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
            </motion.a>
          )}

          {technology.brochures && technology.brochures.map((b, i) => (
            <motion.a
              key={b.file}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              href={b.file}
              download
              className="inline-flex items-center gap-3 mt-8 ml-4 first:ml-0 btn-primary bg-white text-[#1A1A1A] hover:bg-[#F7E7CE] group"
              data-testid={`brochure-download-btn-${i}`}
            >
              <span>{b.label}</span>
              <Download className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
            </motion.a>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 bg-[#1A1A1A]" data-testid="technology-stats">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-3 gap-8 md:gap-16">
            {technology.stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <span className="heading-2 text-[#F7E7CE] block">{stat.value}</span>
                <span className="overline text-white/50 mt-2 block">{stat.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section */}
      {technology.video && (
        <section className="py-24 md:py-32 bg-[#FDF8F5]" data-testid="technology-video">
          <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="text-center mb-12"
            >
              <span className="overline text-[#595959] mb-6 block">Watch</span>
              <h2 className="heading-2 text-[#1A1A1A]">Discover {technology.name}</h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-4xl mx-auto"
            >
              <div className="relative aspect-video bg-[#1A1A1A] overflow-hidden border border-[#E5E5E5]">
                <video
                  className="w-full h-full object-cover"
                  controls
                  poster={technology.heroImage}
                >
                  <source src={technology.video} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Overview Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="technology-overview">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="overline text-[#595959] mb-6 block">Overview</span>
              <h2 className="heading-2 text-[#1A1A1A] mb-8">
                The Science Behind {technology.name}
              </h2>
              <p className="body-text text-[#595959] text-lg leading-relaxed">
                {technology.longDescription}
              </p>

              {technology.products && (
                <div className="mt-12">
                  <span className="overline text-[#595959] mb-4 block">Product Range</span>
                  <div className="flex flex-wrap gap-3">
                    {technology.products.map((product) => (
                      <span
                        key={product}
                        className="px-4 py-2 bg-[#FDF8F5] border border-[#E5E5E5] text-sm"
                      >
                        {product}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {technology.treatments && (
                <div className="mt-12">
                  <span className="overline text-[#595959] mb-4 block">Treatment Options</span>
                  <div className="flex flex-wrap gap-3">
                    {technology.treatments.map((treatment) => (
                      <span
                        key={treatment}
                        className="px-4 py-2 bg-[#FDF8F5] border border-[#E5E5E5] text-sm"
                      >
                        {treatment}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {technology.presets && (
                <div className="mt-12">
                  <span className="overline text-[#595959] mb-4 block">MCT Presets</span>
                  <ul className="space-y-3">
                    {technology.presets.map((preset) => (
                      <li key={preset} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#1A1A1A] mt-0.5 flex-shrink-0" />
                        <span className="body-text text-[#595959]">{preset}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {technology.productImage && (
                <div className="mt-12">
                  <span className="overline text-[#595959] mb-4 block">Product</span>
                  <div className="bg-white p-8 border border-[#E5E5E5]">
                    <img
                      src={technology.productImage}
                      alt={`${technology.name} product`}
                      className="w-full h-auto object-contain max-h-[400px]"
                    />
                  </div>
                </div>
              )}
            </motion.div>

            {/* Right - Benefits */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="bg-[#FDF8F5] p-8 md:p-12 border border-[#E5E5E5]"
            >
              <span className="overline text-[#595959] mb-6 block">Key Benefits</span>
              <ul className="space-y-5">
                {technology.benefits.map((benefit, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-6 h-6 bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="body-text text-[#1A1A1A]">{benefit}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Details Section - For products with detailed info */}
      {technology.productDetails && technology.productDetails.length > 0 && (
        <section className="py-24 md:py-32 bg-[#FDF8F5]" data-testid="product-details-section">
          <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="text-center mb-16"
            >
              <span className="overline text-[#595959] mb-6 block">Product Range</span>
              <h2 className="heading-2 text-[#1A1A1A]">Explore Our Products</h2>
            </motion.div>

            <div className="space-y-24">
              {technology.productDetails.map((product, index) => (
                <motion.div
                  key={product.name}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                >
                  {/* Product Image */}
                  <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="bg-white p-8 md:p-12 border border-[#E5E5E5]">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-auto object-contain max-h-[500px]"
                      />
                    </div>
                  </div>

                  {/* Product Content */}
                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <h3 className="heading-3 text-[#1A1A1A] mb-6">{product.name}</h3>
                    
                    {product.intro && (
                      <p className="body-text text-[#595959] text-lg mb-8 leading-relaxed">
                        {product.intro}
                      </p>
                    )}
                    
                    <p className="body-text text-[#595959] text-lg mb-8 leading-relaxed">
                      {product.description}
                    </p>
                    
                    {product.features && (
                      <ul className="space-y-4">
                        {product.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start gap-4">
                            <div className="w-6 h-6 bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                            <span className="body-text text-[#595959]">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {product.brochure && (
                      <a
                        href={product.brochure}
                        download
                        className="inline-flex items-center gap-3 mt-8 btn-primary bg-[#1A1A1A] text-white hover:bg-[#333] group"
                        data-testid={`product-brochure-${index}`}
                      >
                        <span>{product.brochureLabel || 'Download Brochure'}</span>
                        <Download className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
                      </a>
                    )}

                    {product.resultsImage && (
                      <div className="mt-12">
                        <span className="overline text-[#595959] mb-4 block">Before & After</span>
                        <img
                          src={product.resultsImage}
                          alt={`${product.name} before and after results`}
                          className="w-full h-auto rounded"
                        />
                      </div>
                    )}

                    {product.sections && product.sections.map((section, sIdx) => (
                      <div key={sIdx} className="mt-10">
                        <span className="overline text-[#595959] mb-4 block">{section.title}</span>
                        <ul className="space-y-3">
                          {section.items.map((item, iIdx) => (
                            <li key={iIdx} className="flex items-start gap-3">
                              <div className="w-5 h-5 bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                              <span className="body-text text-[#595959] text-sm">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Results Section */}
      {technology.resultsImage && (
        <section className="py-24 md:py-32 bg-white" data-testid="technology-results">
          <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="text-center mb-12"
            >
              <span className="overline text-[#595959] mb-6 block">Results</span>
              <h2 className="heading-2 text-[#1A1A1A]">Before & After</h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-3xl mx-auto"
            >
              <img
                src={technology.resultsImage}
                alt={`${technology.name} before and after results`}
                className="w-full h-auto"
              />
            </motion.div>
          </div>
        </section>
      )}

      {/* Evolution Product Section (e.g., Aqualyx Face) */}
      {technology.evolutionProduct && (
        <section className="py-24 md:py-32 bg-[#FDF8F5]" data-testid="evolution-product">
          <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="text-center mb-16"
            >
              <span className="overline text-[#595959] mb-6 block">The Evolution</span>
              <h2 className="heading-2 text-[#1A1A1A]">{technology.evolutionProduct.name}</h2>
            </motion.div>

            <div className="max-w-4xl mx-auto">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="body-text text-[#595959] text-lg text-center mb-12 leading-relaxed"
              >
                {technology.evolutionProduct.intro}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mb-12"
              >
                <div className="bg-white p-8 border border-[#E5E5E5]">
                  <img
                    src={technology.evolutionProduct.image}
                    alt={technology.evolutionProduct.name}
                    className="w-full h-auto object-contain max-h-[500px]"
                  />
                </div>
              </motion.div>

              {technology.evolutionProduct.subtitle && (
                <motion.h3
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="heading-3 text-[#1A1A1A] text-center mb-6"
                >
                  {technology.evolutionProduct.subtitle}
                </motion.h3>
              )}

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="body-text text-[#595959] text-lg text-center leading-relaxed"
              >
                {technology.evolutionProduct.description}
              </motion.p>

              {technology.evolutionProduct.resultsImage && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="mt-16"
                >
                  <h3 className="heading-3 text-[#1A1A1A] text-center mb-8">Before & After</h3>
                  <img
                    src={technology.evolutionProduct.resultsImage}
                    alt={`${technology.evolutionProduct.name} before and after results`}
                    className="w-full h-auto"
                  />
                </motion.div>
              )}

              {technology.evolutionProduct.duoImage && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="mt-16"
                >
                  <h3 className="heading-3 text-[#1A1A1A] text-center mb-8">The Ultimate Duo</h3>
                  <img
                    src={technology.evolutionProduct.duoImage}
                    alt="Aqualyx and Aqualyx Face - The Ultimate Duo"
                    className="w-full h-auto"
                  />
                </motion.div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Applications Section */}
      <section className="py-24 md:py-32 bg-[#FDF8F5]" data-testid="technology-applications">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-16"
          >
            <span className="overline text-[#595959] mb-6 block">Treatment Areas</span>
            <h2 className="heading-2 text-[#1A1A1A]">Clinical Applications</h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {technology.applications.map((app, index) => (
              <motion.div
                key={app}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white border border-[#E5E5E5] p-6 text-center hover:border-[#1A1A1A] transition-colors duration-300"
              >
                <span className="body-text text-[#1A1A1A]">{app}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#1A1A1A]" data-testid="technology-cta">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="overline text-white/50 mb-6 block">Ready to Transform Your Practice?</span>
            <h2 className="heading-2 text-white mb-8 max-w-2xl mx-auto">
              Bring {technology.name} to Your Clinic
            </h2>
            <p className="body-text text-white/70 text-lg max-w-xl mx-auto mb-12">
              Contact our team to learn about partnership opportunities, training programs, 
              and how {technology.name} can benefit your patients.
            </p>
            <Link
              to="/#contact"
              className="btn-primary bg-white text-[#1A1A1A] hover:bg-[#F7E7CE] inline-flex items-center gap-3 group"
              data-testid="technology-contact-btn"
            >
              <span>Get Started</span>
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default TechnologyPage;
